import {
  SURVEY_OTHER_VALUE,
  type Survey,
  type SurveyAnswerValue,
  type SurveyQuestion,
  type SurveyResponse,
} from '@citizen-walk/shared-types';
import { csvEscape } from './surveyResponseExport';

/**
 * Survey response 集計 helper (Sprint BG-DataSettings 12、GAP-A10 完全化)。
 *
 * 旧 v1 `old/apps/admin/src/lib/surveyResponses.ts` の aggregateResponses 相当を v2 で再実装。
 *
 * 仕様:
 *   - 回答全体の合計件数
 *   - 質問ごとの: answered (回答済) / unanswered 件数
 *   - 選択肢系 (radio / dropdown / checkbox): option ごとの件数 + その他フリーテキスト出現回数
 *   - 数値系 (linear_scale / rating / number): min / max / mean / count
 *   - 自由記述系 (short_text / long_text): textCount (空文字以外の回答数)
 *   - 日付 / 時刻: 各値の出現回数
 *   - 集計を CSV 1 file にまとめて export (existing surveyResponseExport の raw CSV とは別出力)
 *
 * 旧 v1 では Excel multi-sheet で出力していたが、v2 では CSV 1 file に縦持ち集計表として出力
 * (xlsx 依存を回避、CLAUDE.md 制約)。
 */

/** option ごとの集計 (radio / dropdown / checkbox)。 */
export interface OptionTally {
  /** 選択肢の文言 (回答の保存値)。 */
  label: string;
  count: number;
  /**
   * 今の設計に無い文言の回答 (回答が集まった後に選択肢の文言を直した / 消した場合)。
   * 数え漏らさないよう、設計の選択肢の後ろに別枠で並べる。
   */
  notInOptions?: boolean;
}

/** 質問 1 件分の集計結果。 */
export type QuestionAggregation =
  | {
      questionId: string;
      title: string;
      type: 'short_text' | 'long_text';
      answeredCount: number;
      unansweredCount: number;
      textCount: number;
    }
  | {
      questionId: string;
      title: string;
      type: 'radio' | 'dropdown' | 'checkbox';
      answeredCount: number;
      unansweredCount: number;
      options: OptionTally[];
      /** allowOther = true 時の「その他」テキスト件数 (radio / checkbox)。 */
      otherCount: number;
    }
  | {
      questionId: string;
      title: string;
      type: 'linear_scale' | 'rating' | 'number';
      answeredCount: number;
      unansweredCount: number;
      min: number | null;
      max: number | null;
      mean: number | null;
    }
  | {
      questionId: string;
      title: string;
      type: 'date' | 'time';
      answeredCount: number;
      unansweredCount: number;
      /** value → count map (出現順保持)。 */
      valueTallies: Array<{ value: string; count: number }>;
    };

export interface ResponseAggregation {
  totalResponses: number;
  questions: QuestionAggregation[];
}

/** answer が「回答あり」と判定できるか (空文字 / 空配列は未回答扱い)。 */
function isAnswered(value: SurveyAnswerValue | undefined): boolean {
  if (!value) return false;
  switch (value.type) {
    case 'text':
      return value.value !== '';
    case 'choice':
      return value.value !== '';
    case 'choices':
      return value.values.length > 0;
    case 'number':
      return Number.isFinite(value.value);
    case 'date':
    case 'time':
      return value.value !== '';
    default:
      return false;
  }
}

function aggregateOption(
  question: SurveyQuestion,
  responses: SurveyResponse[],
): QuestionAggregation {
  // narrow: must be choice type
  if (question.type !== 'radio' && question.type !== 'dropdown' && question.type !== 'checkbox') {
    throw new Error('aggregateOption: not a choice question');
  }
  const optionMap = new Map<string, OptionTally>();
  for (const o of question.options) {
    optionMap.set(o.label, { label: o.label, count: 0 });
  }
  const allowOther = question.type !== 'dropdown' && question.allowOther === true;
  let answered = 0;
  let unanswered = 0;
  let otherCount = 0;

  const tallyOf = (label: string) => {
    // 「その他」(記入欄付き) は選択肢ではなく otherCount で数える。
    if (allowOther && label === SURVEY_OTHER_VALUE) {
      otherCount += 1;
      return;
    }
    let tally = optionMap.get(label);
    if (!tally) {
      tally = { label, count: 0, notInOptions: true };
      optionMap.set(label, tally);
    }
    tally.count += 1;
  };

  for (const r of responses) {
    const v = r.answers[question.id];
    if (!isAnswered(v)) {
      unanswered += 1;
      continue;
    }
    answered += 1;
    if (v?.type === 'choice') tallyOf(v.value);
    else if (v?.type === 'choices') v.values.forEach(tallyOf);
  }
  return {
    questionId: question.id,
    title: question.title,
    type: question.type,
    answeredCount: answered,
    unansweredCount: unanswered,
    options: Array.from(optionMap.values()),
    otherCount,
  };
}

function aggregateNumeric(
  question: SurveyQuestion,
  responses: SurveyResponse[],
): QuestionAggregation {
  if (
    question.type !== 'linear_scale' &&
    question.type !== 'rating' &&
    question.type !== 'number'
  ) {
    throw new Error('aggregateNumeric: not a numeric question');
  }
  let answered = 0;
  let unanswered = 0;
  let min: number | null = null;
  let max: number | null = null;
  let sum = 0;
  for (const r of responses) {
    const v = r.answers[question.id];
    if (!isAnswered(v) || v?.type !== 'number') {
      unanswered += 1;
      continue;
    }
    answered += 1;
    const n = v.value;
    sum += n;
    if (min === null || n < min) min = n;
    if (max === null || n > max) max = n;
  }
  const mean = answered > 0 ? sum / answered : null;
  return {
    questionId: question.id,
    title: question.title,
    type: question.type,
    answeredCount: answered,
    unansweredCount: unanswered,
    min,
    max,
    mean,
  };
}

function aggregateText(question: SurveyQuestion, responses: SurveyResponse[]): QuestionAggregation {
  if (question.type !== 'short_text' && question.type !== 'long_text') {
    throw new Error('aggregateText: not a text question');
  }
  let answered = 0;
  let unanswered = 0;
  for (const r of responses) {
    const v = r.answers[question.id];
    if (isAnswered(v)) answered += 1;
    else unanswered += 1;
  }
  return {
    questionId: question.id,
    title: question.title,
    type: question.type,
    answeredCount: answered,
    unansweredCount: unanswered,
    textCount: answered,
  };
}

function aggregateDateTime(
  question: SurveyQuestion,
  responses: SurveyResponse[],
): QuestionAggregation {
  if (question.type !== 'date' && question.type !== 'time') {
    throw new Error('aggregateDateTime: not a date/time question');
  }
  let answered = 0;
  let unanswered = 0;
  const tallies = new Map<string, number>();
  for (const r of responses) {
    const v = r.answers[question.id];
    if (!isAnswered(v) || (v?.type !== 'date' && v?.type !== 'time')) {
      unanswered += 1;
      continue;
    }
    answered += 1;
    tallies.set(v.value, (tallies.get(v.value) ?? 0) + 1);
  }
  return {
    questionId: question.id,
    title: question.title,
    type: question.type,
    answeredCount: answered,
    unansweredCount: unanswered,
    valueTallies: Array.from(tallies.entries()).map(([value, count]) => ({ value, count })),
  };
}

/**
 * Survey response 配列から集計を生成。
 * 質問が 0 件 / 回答が 0 件 でも安全に空集計を返す。
 */
export function aggregateResponses(
  survey: Pick<Survey, 'questions'>,
  responses: SurveyResponse[],
): ResponseAggregation {
  const questions: QuestionAggregation[] = survey.questions.map((q) => {
    switch (q.type) {
      case 'radio':
      case 'dropdown':
      case 'checkbox':
        return aggregateOption(q, responses);
      case 'linear_scale':
      case 'rating':
      case 'number':
        return aggregateNumeric(q, responses);
      case 'short_text':
      case 'long_text':
        return aggregateText(q, responses);
      case 'date':
      case 'time':
        return aggregateDateTime(q, responses);
    }
  });
  return {
    totalResponses: responses.length,
    questions,
  };
}

function formatMean(n: number | null): string {
  if (n === null) return '';
  // 小数 2 桁まで (旧 v1 互換)
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/**
 * 集計を CSV 文字列に変換 (縦持ち、1 行 1 集計値)。
 *
 * format:
 *   ヘッダー: "questionId,questionType,questionTitle,category,key,value"
 *   各 row の category は { responses, answered, unanswered, option, other, min, max, mean,
 *     text, dateValue, timeValue } のいずれか
 *
 * 1 行目に "全体,,,responses,,<件数>" を必ず含む (旧 v1 互換)。
 */
export function aggregationToCsv(agg: ResponseAggregation): string {
  const lines: string[] = [];
  lines.push(
    ['questionId', 'questionType', 'questionTitle', 'category', 'key', 'value']
      .map(csvEscape)
      .join(','),
  );
  lines.push(
    ['', '', '全体', 'responses', '', String(agg.totalResponses)].map(csvEscape).join(','),
  );

  for (const q of agg.questions) {
    const head = [q.questionId, q.type, q.title];
    lines.push([...head, 'answered', '', String(q.answeredCount)].map(csvEscape).join(','));
    lines.push([...head, 'unanswered', '', String(q.unansweredCount)].map(csvEscape).join(','));
    if (q.type === 'radio' || q.type === 'dropdown' || q.type === 'checkbox') {
      for (const o of q.options) {
        lines.push(
          [
            ...head,
            'option',
            o.notInOptions ? `${o.label}（現在の選択肢にない）` : o.label,
            String(o.count),
          ]
            .map(csvEscape)
            .join(','),
        );
      }
      if (q.otherCount > 0) {
        lines.push([...head, 'other', '', String(q.otherCount)].map(csvEscape).join(','));
      }
    } else if (q.type === 'linear_scale' || q.type === 'rating' || q.type === 'number') {
      lines.push(
        [...head, 'min', '', q.min === null ? '' : String(q.min)].map(csvEscape).join(','),
      );
      lines.push(
        [...head, 'max', '', q.max === null ? '' : String(q.max)].map(csvEscape).join(','),
      );
      lines.push([...head, 'mean', '', formatMean(q.mean)].map(csvEscape).join(','));
    } else if (q.type === 'short_text' || q.type === 'long_text') {
      lines.push([...head, 'text', '', String(q.textCount)].map(csvEscape).join(','));
    } else if (q.type === 'date' || q.type === 'time') {
      for (const t of q.valueTallies) {
        lines.push(
          [...head, q.type === 'date' ? 'dateValue' : 'timeValue', t.value, String(t.count)]
            .map(csvEscape)
            .join(','),
        );
      }
    }
  }

  return lines.join('\r\n');
}

/** 集計 CSV ファイル名 `survey_<id>_aggregation_<yyyymmdd_hhmm>.csv`。 */
export function buildAggregationFilename(surveyId: string, now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `_${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `survey_${surveyId}_aggregation_${stamp}.csv`;
}
